import React, { useState, useEffect } from 'react';
import './ElectronControls.css';

const ElectronControls = () => {
  const [isElectron, setIsElectron] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isStealthMode, setIsStealthMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAlwaysOnTop, setIsAlwaysOnTop] = useState(false);

  useEffect(() => {
    // Check if running in Electron
    const isElectronApp = window.navigator.userAgent.toLowerCase().includes('electron');
    setIsElectron(isElectronApp);

    // Load saved settings
    const savedBrightness = window.localStorage.getItem('electron_brightness');
    const savedMobileView = window.localStorage.getItem('electron_mobile_view');
    const savedStealthMode = window.localStorage.getItem('electron_stealth_mode');
    const savedAlwaysOnTop = window.localStorage.getItem('electron_always_on_top');

    if (savedBrightness) setBrightness(Number(savedBrightness));
    if (savedMobileView) setIsMobileView(savedMobileView === 'true');
    if (savedStealthMode) setIsStealthMode(savedStealthMode === 'true');
    if (savedAlwaysOnTop) {
      const alwaysOnTopValue = savedAlwaysOnTop === 'true';
      setIsAlwaysOnTop(alwaysOnTopValue);
      if (window.electron?.setAlwaysOnTop && isElectronApp) {
        window.electron.setAlwaysOnTop(alwaysOnTopValue);
      }
    }
  }, []);

  useEffect(() => {
    // Apply transparency - convert brightness to opacity
    // 100% brightness = 0% opacity (fully opaque)
    // 0% brightness = 100% opacity (fully transparent)
    const opacity = (100 - brightness) / 10;
    document.documentElement.style.setProperty('--background-opacity', opacity.toString());
    
    if (brightness < 100) {
      document.body.classList.add('transparent-mode');
    } else {
      document.body.classList.remove('transparent-mode');
    }
    
    window.localStorage.setItem('electron_brightness', brightness.toString());
  }, [brightness]);

  useEffect(() => {
    // Apply mobile view
    if (isMobileView) {
      document.body.classList.add('mobile-view-mode');
    } else {
      document.body.classList.remove('mobile-view-mode');
    }
    window.localStorage.setItem('electron_mobile_view', isMobileView.toString());
  }, [isMobileView]);

  useEffect(() => {
    // Apply stealth mode
    if (isStealthMode) {
      document.body.classList.add('stealth-mode');
      // Hide app from screenshots if running in Electron
      if (window.electron?.setContentProtection) {
        window.electron.setContentProtection(true);
      }
    } else {
      document.body.classList.remove('stealth-mode');
      if (window.electron?.setContentProtection) {
        window.electron.setContentProtection(false);
      }
    }
    window.localStorage.setItem('electron_stealth_mode', isStealthMode.toString());
  }, [isStealthMode]);

  const handleAlwaysOnTop = () => {
    const newValue = !isAlwaysOnTop;
    setIsAlwaysOnTop(newValue);
    if (window.electron?.setAlwaysOnTop) {
      window.electron.setAlwaysOnTop(newValue);
    }
  };

  const handleMinimize = () => {
    if (window.electron?.minimize) {
      window.electron.minimize();
    }
  };

  const handleMaximize = () => {
    if (window.electron?.maximize) {
      window.electron.maximize();
    }
  };

  const handleClose = () => {
    if (window.electron?.close) {
      window.electron.close();
    }
  };

  // Don't render if not in Electron
  if (!isElectron) return null;

  return (
    <div className={`electron-controls ${isExpanded ? 'expanded' : ''}`}>
      {/* Window Control Buttons - Always Visible */}
      <div className="window-buttons">
        <button 
          className="window-btn minimize-btn"
          onClick={handleMinimize}
          title="Minimize"
        >
          −
        </button>
        <button 
          className="window-btn maximize-btn"
          onClick={handleMaximize}
          title="Maximize/Restore"
        >
          □
        </button>
        <button 
          className="window-btn close-btn"
          onClick={handleClose}
          title="Close"
        >
          ✕
        </button>
      </div>

      <button 
        className="controls-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        title="Toggle Controls"
      >
        ⚙️
      </button>

      {isExpanded && (
        <div className="controls-panel">
          {/* Brightness Control */}
          <div className="control-group">
            <div className="control-header">
              <span className="control-icon">☀️</span>
              <span className="control-label">Brightness</span>
              <span className="control-value">{brightness}%</span>
            </div>
            <input
              type="range"
              min="20"
              max="100"
              value={brightness}
              onChange={(e) => setBrightness(Number(e.target.value))}
              className="brightness-slider"
            />
          </div>

          {/* Stealth Mode Toggle */}
          <div className="control-group">
            <div className="control-header">
              <span className="control-icon">🕶️</span>
              <span className="control-label">Stealth Mode</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isStealthMode}
                  onChange={(e) => setIsStealthMode(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p className="control-description">
              Hides content from screenshots and screen recording
            </p>
          </div>

          {/* Additional Window Controls */}
          <div className="control-group window-controls">
            <button 
              className={`window-control-btn ${isAlwaysOnTop ? 'active' : ''}`}
              onClick={handleAlwaysOnTop}
              title="Always on Top"
            >
              📌 {isAlwaysOnTop ? 'Pinned' : 'Pin Window'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectronControls;